import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    Loader2, Search, Trash2, Calendar, X, ExternalLink, Settings as SettingsIcon,
    ChevronRight, History, ShieldAlert, CheckCircle2, ChevronLeft, AlertCircle, Info,
    User as UserIcon, UserPlus, Clock, Bell, Trash, Shield, ArrowLeft, MessageSquare, Newspaper, Trophy, ListFilter,
    Image as ImageIcon, Eye, EyeOff, Coins, FileText, Edit2, Brain
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useToast } from '../../context/ToastContext';
import { 
    fetchAllUsers, fetchAdminStats, fetchAllNews, deleteNewsPermanently, 
    updateUserRole, deleteUserPermanently, fetchUserFullActivity, fetchNewsReports,
    fetchRecentAdminAlerts, toggleNewsVisibility, updateNewsItem
} from '../../services/adminService';
import { translateNewsContent } from '../../services/geminiService';
import { supabase } from '../../services/authService';
import { pointService } from '../../services/pointService';
import { User, NewsItem } from '../../types';
import { AdminSidebar, AdminTab } from './AdminSidebar';
import { AdminHeader } from './AdminHeader';
import { FeedNewsCard } from '../News/FeedNewsCard';
import { OverviewTab } from './tabs/OverviewTab';
import { UsersTab } from './tabs/UsersTab';
import { PlayersTab } from './tabs/PlayersTab';
import { MatchAnalysisTab } from './tabs/MatchAnalysisTab';
import { NoticesTab } from './tabs/NoticesTab';
import { ReportsTab } from './tabs/ReportsTab';
import { SettingsTab } from './tabs/SettingsTab';
import { InquiriesTab } from './tabs/InquiriesTab';
import { HighlightsTab } from './tabs/HighlightsTab';
import { ShortsTab } from './tabs/ShortsTab';
import { GameStoreTab } from './tabs/GameStoreTab';
import { FootyMasterTab } from './tabs/FootyMasterTab';
import { TotoTab } from './tabs/TotoTab';
import { BannerTab } from './tabs/BannerTab';
// Modals
import { AdminActionModal } from './modals/AdminActionModal';
import { AdminUserAuditModal } from './modals/AdminUserAuditModal';
import { AdminNewsModals } from './modals/AdminNewsModals';
import { AdminNewsAuditModal } from './modals/AdminNewsAuditModal';
import { AnalysisModal } from './modals/AnalysisModal';

import { at } from '../../i18nAdmin';
import { calculateTimeAgo } from '../../services/formatService';
import { clearCache } from '../../services/storageService';

interface AdminViewProps {
    onExit: () => void;
}

type AdminActionType = 'DELETE_USER' | 'DELETE_NEWS' | 'UPDATE_ROLE' | 'AWARD_POINTS' | 'NONE';

interface ActionModalState {
    type: AdminActionType;
    isOpen: boolean;
    data: any;
    title: string;
    description: string;
    confirmLabel: string;
    isDanger: boolean;
}

type UserAuditTab = 'news' | 'news_comments' | 'match_comments';

export const AdminView: React.FC<AdminViewProps> = ({ onExit }) => {
    const { user, isLoading: isAuthLoading } = useAuth();
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState<AdminTab>('overview');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [stats, setStats] = useState({ users: 0, posts: 0, comments: 0, reports: 0, usersGrowth: 0, activityChange: '0' });
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [allNews, setAllNews] = useState<NewsItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialLoaded, setIsInitialLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Alerts Drawer States
    const [isAlertsOpen, setIsAlertsOpen] = useState(false);
    const [isAlertsAnimating, setIsAlertsAnimating] = useState(false);
    const [alerts, setAlerts] = useState<any[]>([]);
    const [loadingAlerts, setLoadingAlerts] = useState(false);

    const [userPageSize] = useState(30);
    const [userCurrentPage, setUserCurrentPage] = useState(1);
    const [userFilterDate, setUserFilterDate] = useState('');

    const [contentPageSize] = useState(30);
    const [contentCurrentPage, setContentCurrentPage] = useState(1);
    const [contentFilterDate, setContentFilterDate] = useState('');
    
    // User Detail / Audit Modal States
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isAuditModalOpen, setIsAuditModalOpen] = useState(false); 
    const [userActivity, setUserActivity] = useState<{news: any[], comments: any[]} | null>(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [isAuditAnimating, setIsAuditAnimating] = useState(false);
    
    const [auditTab, setAuditTab] = useState<UserAuditTab>('news');
    const [auditPage, setAuditPage] = useState(1);
    const [auditPageSize, setAuditPageSize] = useState(10);
    const [auditDateFilter, setAuditDateFilter] = useState('');

    const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
    const [isNewsAuditOpen, setIsNewsAuditOpen] = useState(false);
    const [isNewsAuditAnimating, setIsNewsAuditAnimating] = useState(false);
    const [newsReports, setNewsReports] = useState<any[]>([]);
    const [isReportsLoading, setIsReportsLoading] = useState(false);

    // News Preview & Edit States
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isPreviewAnimating, setIsPreviewAnimating] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isEditAnimating, setIsEditAnimating] = useState(false);
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({
        title_ko: '',
        summary_ko: '',
        content_ko: '',
        type: ''
    });

    const [actionModal, setActionModal] = useState<ActionModalState>({
        type: 'NONE', isOpen: false, data: null, title: '', description: '', confirmLabel: '', isDanger: false
    });
    const [awardAmount, setAwardAmount] = useState(100);
    const [awardReason, setAwardReason] = useState('Admin Reward');
    const [isActionAnimating, setIsActionAnimating] = useState(false);
    const [isActionPending, setIsActionPending] = useState(false);
    const mainContentRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (mainContentRef.current) {
            mainContentRef.current.scrollTop = 0;
        }
    }, [activeTab]);

    const loadInitialData = useCallback(async () => {
        if (!user || user.role !== 'admin') return;
        
        try {
            const [s, u, n] = await Promise.all([
                fetchAdminStats(),
                fetchAllUsers(),
                fetchAllNews()
            ]);
            setStats(s as any);
            setAllUsers(u);
            setAllNews(n);
        } catch (e) {
            console.error("Admin data load failed:", e);
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (!isAuthLoading && user?.role !== 'admin') {
            onExit();
            return;
        }

        if (user?.role === 'admin' && !isInitialLoaded) {
            loadInitialData().then(() => setIsInitialLoaded(true));
        }
    }, [user, isAuthLoading, onExit, loadInitialData, isInitialLoaded]);

    const handleOpenAlerts = async () => {
        setLoadingAlerts(true);
        setIsAlertsOpen(true);
        setTimeout(() => setIsAlertsAnimating(true), 10);
        try {
            const recentAlerts = await fetchRecentAdminAlerts();
            setAlerts(recentAlerts);
        } catch (e) {
            setAlerts([]);
        } finally {
            setLoadingAlerts(false);
        }
    };

    const handleRefresh = async () => {
        if (!user || user.role !== 'admin') return;
        
        setIsLoading(true);
        try {
            switch (activeTab) {
                case 'overview':
                    const [s, u, n] = await Promise.all([
                        fetchAdminStats(),
                        fetchAllUsers(),
                        fetchAllNews()
                    ]);
                    setStats(s as any);
                    setAllUsers(u);
                    setAllNews(n);
                    break;
                case 'users':
                    const users = await fetchAllUsers();
                    setAllUsers(users);
                    break;
                case 'content':
                    const news = await fetchAllNews();
                    setAllNews(news);
                    break;
                default:
                    // For other tabs like inquiries, reports etc. 
                    // Since they might manage their own state or we just reload all for now
                    await loadInitialData();
                    break;
            }
            showToast('success', at(lang, 'refreshSuccess'));
        } catch (e) {
            console.error("Admin refresh failed:", e);
            showToast('error', at(lang, 'updateFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseAlerts = () => {
        setIsAlertsAnimating(false);
        setTimeout(() => setIsAlertsOpen(false), 500);
    };

    const triggerActionModal = (isOpen: boolean) => {
        if (isOpen) {
            setActionModal(prev => ({ ...prev, isOpen: true }));
            setTimeout(() => setIsActionAnimating(true), 10);
        } else {
            setIsActionAnimating(false);
            setTimeout(() => setActionModal(prev => ({ ...prev, isOpen: false })), 500);
        }
    };

    const triggerAuditModal = (isOpen: boolean) => {
        if (isOpen) {
            setIsAuditModalOpen(true);
            setTimeout(() => setIsAuditAnimating(true), 10);
        } else {
            setIsAuditAnimating(false);
            setAuditDateFilter('');
            setTimeout(() => setIsAuditModalOpen(false), 500);
        }
    };

    const triggerNewsAuditModal = (isOpen: boolean) => {
        if (isOpen) {
            setIsNewsAuditOpen(true);
            setTimeout(() => setIsNewsAuditAnimating(true), 10);
        } else {
            setIsNewsAuditAnimating(false);
            setTimeout(() => setIsNewsAuditOpen(false), 500);
        }
    };

    const triggerPreviewModal = async (isOpen: boolean, news?: NewsItem) => {
        if (isOpen && news) {
        try {
            const { data } = await supabase.from('news_list').select('*').eq('id', news.id).single();
            if (data) {
                setSelectedNews({
                    ...news,
                    content: lang === 'ko' ? data.content_ko : data.content_en,
                    summary: (data.type === 'magazine' || data.type === 'webtoon') ? null : (lang === 'ko' ? data.summary_ko : data.summary_en)
                });
            } else {
                    setSelectedNews(news);
                }
            } catch (e) {
                setSelectedNews(news);
            }
            setIsPreviewOpen(true);
            setTimeout(() => setIsPreviewAnimating(true), 10);
        } else {
            setIsPreviewAnimating(false);
            setTimeout(() => setIsPreviewOpen(false), 500);
        }
    };

    const triggerEditModal = async (isOpen: boolean, news?: NewsItem) => {
        if (isOpen && news) {
            setSelectedNews(news);
            try {
                const { data } = await supabase.from('news_list').select('*').eq('id', news.id).single();
                if (data) {
                    setEditFormData({
                        title_ko: data.title_ko || '',
                        summary_ko: data.summary_ko || '',
                        content_ko: data.content_ko || '',
                        type: data.type || ''
                    });
                }
            } catch (e) {
                console.error("Failed to fetch full news data", e);
            }
            setIsEditOpen(true);
            setTimeout(() => setIsEditAnimating(true), 10);
        } else {
            setIsEditAnimating(false);
            setTimeout(() => setIsEditOpen(false), 500);
        }
    };

    const handleUpdateNews = async () => {
        if (!selectedNews) return;
        setIsActionPending(true);
        try {
            const newsId = selectedNews.id.toString();
            
            // Auto-translate using AI
            const translations = await translateNewsContent(
                editFormData.title_ko,
                editFormData.summary_ko,
                editFormData.content_ko
            );

            const finalData = {
                ...editFormData,
                ...(translations || {})
            };

            await updateNewsItem(newsId, finalData);
            
            showToast('success', translations ? at(lang, 'configSaved') : 'Saved (Translation Failed)');
            
            // Refresh local state
            setAllNews(prev => prev.map(n => n.id === newsId ? { 
                ...n, 
                title: editFormData.title_ko,
                summary: editFormData.summary_ko,
                type: editFormData.type
            } : n));
            
            loadInitialData();
            triggerEditModal(false);
        } catch (e: any) {
            console.error("News update failed:", e);
            showToast('error', `Update Failed: ${e.message}`);
        } finally {
            setIsActionPending(false);
        }
    };

    const openConfirm = (type: AdminActionType, data: any) => {
        let config: Partial<ActionModalState> = { type, data };
        
        switch(type) {
            case 'DELETE_USER':
                config.title = at(lang, 'deletePersonnel');
                config.description = at(lang, 'deleteDesc');
                config.confirmLabel = at(lang, 'terminateUser');
                config.isDanger = true;
                break;
            case 'DELETE_NEWS':
                config.title = at(lang, 'terminateIntelligence');
                config.description = at(lang, 'deleteBroadcastDesc');
                config.confirmLabel = at(lang, 'terminateIntelligence');
                config.isDanger = true;
                break;
            case 'UPDATE_ROLE':
                config.title = at(lang, 'changeAccess');
                config.description = at(lang, 'accessLevelDesc');
                config.confirmLabel = at(lang, 'save');
                config.isDanger = data.role === 'admin'; 
                break;
            case 'AWARD_POINTS':
                config.title = at(lang, 'awardPoints');
                config.description = at(lang, 'awardPointsUserDesc').replace('{{name}}', data.name);
                config.confirmLabel = at(lang, 'awardAction');
                config.isDanger = false;
                setAwardAmount(100);
                setAwardReason(at(lang, 'adminReward'));
                break;
        }
        
        setActionModal(prev => ({ ...prev, ...config, isOpen: true }));
        setTimeout(() => setIsActionAnimating(true), 10);
    };

    const handleActionConfirm = async () => {
        setIsActionPending(true);
        const { type, data } = actionModal;
        try {
            if (type === 'DELETE_USER') {
                await deleteUserPermanently(data.id);
                setAllUsers(prev => prev.filter(u => u.id !== data.id));
                showToast('success', at(lang, 'deleteUserSuccess'));
            } else if (type === 'DELETE_NEWS') {
                await deleteNewsPermanently(data.id);
                setAllNews(prev => prev.filter(n => n.id !== data.id));
                clearCache();
                showToast('success', at(lang, 'deleteNewsSuccess'));
                if (selectedNews?.id === data.id) triggerNewsAuditModal(false);
            } else if (type === 'UPDATE_ROLE') {
                const newRole = data.role === 'admin' ? 'user' : 'admin';
                console.log(`Attempting to update user ${data.id} role to ${newRole}`);
                await updateUserRole(data.id, newRole);
                setAllUsers(prev => prev.map(u => u.id === data.id ? { ...u, role: newRole } : u));
                showToast('success', at(lang, 'configSaved'));
            } else if (type === 'AWARD_POINTS') {
                await pointService.awardPoints(data.id, awardAmount, awardReason);
                showToast('success', lang === 'ko' ? `${awardAmount}${at(lang, 'awardSuccess')}` : `${awardAmount} ${at(lang, 'awardSuccess')}`);
            }
            triggerActionModal(false);
        } catch (e: any) {
            console.error(`Admin action ${type} failed:`, e);
            const errorMessage = e.message || 'Unknown error occurred';
            const hint = e.code === '42501' ? ' (Check RLS Policies)' : '';
            showToast('error', `${at(lang, 'operationFailed') || 'Operation Failed'}: ${errorMessage}${hint}`);
        } finally {
            setIsActionPending(false);
        }
    };

    const handleToggleNewsVisibility = async (newsId: string, currentHidden: boolean) => {
        try {
            const nextHidden = !currentHidden;
            await toggleNewsVisibility(newsId, nextHidden);
            setAllNews(prev => prev.map(n => n.id === newsId ? { ...n, is_hidden: nextHidden } : n));
            showToast('success', at(lang, 'visibilityChanged'));
        } catch (e) {
            showToast('error', "Failed to update visibility");
        }
    };

    const handleViewUserDetail = async (targetUser: User) => {
        setSelectedUser(targetUser);
        setAuditTab('news');
        setAuditPage(1);
        setAuditDateFilter('');
        setIsDetailLoading(true);
        setUserActivity(null);
        triggerAuditModal(true);
        try {
            const activity = await fetchUserFullActivity(targetUser.id);
            setUserActivity({ news: activity.news, comments: activity.comments });
        } catch (e) {
            console.error(e);
        } finally {
            setIsDetailLoading(false);
        }
    };

    const handleViewNewsReports = async (news: NewsItem) => {
        setSelectedNews(news);
        setIsReportsLoading(true);
        triggerNewsAuditModal(true);
        try {
            const reports = await fetchNewsReports(news.id);
            setNewsReports(reports);
        } catch (e) {
            setNewsReports([]);
        } finally {
            setIsReportsLoading(false);
        }
    };

    const filteredUsers = useMemo(() => {
        return allUsers.filter(u => {
            const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesDate = userFilterDate ? u.joinDate?.startsWith(userFilterDate) : true;
            return matchesSearch && matchesDate;
        });
    }, [allUsers, searchTerm, userFilterDate]);

    const totalUserPages = Math.ceil(filteredUsers.length / userPageSize);
    const paginatedUsers = useMemo(() => {
        const start = (userCurrentPage - 1) * userPageSize;
        return filteredUsers.slice(start, start + userPageSize);
    }, [filteredUsers, userCurrentPage, userPageSize]);

    const filteredNews = useMemo(() => {
        return allNews.filter(n => {
            const matchesSearch = n.title.toLowerCase().includes(searchTerm.toLowerCase()) || n.source?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesDate = contentFilterDate ? n.publishedAt?.startsWith(contentFilterDate) : true;
            return matchesSearch && matchesDate;
        });
    }, [allNews, searchTerm, contentFilterDate]);

    const totalContentPages = Math.ceil(filteredNews.length / contentPageSize);
    const paginatedNews = useMemo(() => {
        const start = (contentCurrentPage - 1) * contentPageSize;
        return filteredNews.slice(start, start + contentPageSize);
    }, [filteredNews, contentCurrentPage, contentPageSize]);

    // User Audit Modal Logic
    const auditStats = useMemo(() => {
        if (!userActivity) return { news: 0, news_comments: 0, match_comments: 0 };
        return {
            news: userActivity.news.length,
            news_comments: userActivity.comments.filter(c => c.type === 'News').length,
            match_comments: userActivity.comments.filter(c => c.type === 'Match').length
        };
    }, [userActivity]);

    const activeAuditList = useMemo(() => {
        if (!userActivity) return [];
        let list: any[] = [];
        if (auditTab === 'news') list = userActivity.news;
        else if (auditTab === 'news_comments') list = userActivity.comments.filter(c => c.type === 'News');
        else if (auditTab === 'match_comments') list = userActivity.comments.filter(c => c.type === 'Match');
        
        if (auditDateFilter) {
            list = list.filter(item => (item.created_at || item.published_at || '').startsWith(auditDateFilter));
        }
        
        return list;
    }, [userActivity, auditTab, auditDateFilter]);

    const totalAuditPages = Math.ceil(activeAuditList.length / auditPageSize);
    const paginatedAuditItems = useMemo(() => {
        const start = (auditPage - 1) * auditPageSize;
        return activeAuditList.slice(start, start + auditPageSize);
    }, [activeAuditList, auditPage, auditPageSize]);

    if (isLoading || isAuthLoading) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center bg-white min-h-[500px]">
                <Loader2 className="animate-spin text-rose-500 mb-6" size={48} strokeWidth={2.5} />
                <p className="text-sm font-black text-slate-900 uppercase tracking-[0.2em]">{at(lang, 'authorizing')}</p>
            </div>
        );
    }

    return (
        <div className="flex-1 flex h-full bg-slate-50/50 overflow-hidden relative font-sans text-slate-900">
            <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} onExit={onExit} isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
                <AdminHeader 
                    user={user} 
                    onMenuClick={() => setIsSidebarOpen(true)} 
                    onRefresh={handleRefresh} 
                    onBellClick={handleOpenAlerts}
                    title={at(lang, activeTab)} 
                    hasNewAlerts={alerts.length > 0}
                />

                <main ref={mainContentRef} className="flex-1 overflow-y-auto p-6 md:p-8 lg:p-12 custom-scrollbar">
                    <div className="max-w-[1600px] mx-auto pb-24">
                        {activeTab === 'overview' && (
                            <OverviewTab
                                stats={stats}
                                allUsers={allUsers}
                                onTabSwitch={setActiveTab}
                            />
                        )}

                        {activeTab === 'banners' && <BannerTab />}

                        {activeTab === 'users' && (
                            <UsersTab 
                                lang={lang}
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                userFilterDate={userFilterDate}
                                setUserFilterDate={setUserFilterDate}
                                setUserCurrentPage={setUserCurrentPage}
                                paginatedUsers={paginatedUsers}
                                userCurrentPage={userCurrentPage}
                                totalUserPages={totalUserPages}
                                handleViewUserDetail={handleViewUserDetail}
                                openConfirm={openConfirm}
                            />
                        )}

                        {activeTab === 'players' && (
                            <PlayersTab lang={lang} />
                        )}

                        {activeTab === 'matchAnalysis' && (
                            <MatchAnalysisTab />
                        )}

                        {activeTab === 'content' && (
                            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div>
                                        <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">{at(lang, 'intelligence')}</h1>
                                        <p className="text-sm font-medium text-slate-500 mt-1">{at(lang, 'intelligenceDesc')}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
                                            <Calendar size={14} className="text-rose-500" />
                                            <input 
                                                type="date" 
                                                className="bg-transparent text-[11px] font-black uppercase text-slate-600 outline-none cursor-pointer"
                                                value={contentFilterDate}
                                                onChange={e => { setContentFilterDate(e.target.value); setContentCurrentPage(1); }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                                    <div className="p-8 border-b border-slate-100">
                                        <div className="relative group">
                                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                            <input 
                                                type="text" 
                                                placeholder={at(lang, 'searchBroadcast')} 
                                                className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10"
                                                value={searchTerm}
                                                onChange={e => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                                    <th className="px-8 py-5 min-w-[200px] max-w-[300px]">{at(lang, 'broadcastingAsset')}</th>
                                                    <th className="px-6 py-5 min-w-[120px]">{at(lang, 'sector')}</th>
                                                    <th className="px-6 py-5 min-w-[140px]">{at(lang, 'timestamp')}</th>
                                                    <th className="px-8 py-5 text-right min-w-[120px]">{at(lang, 'ops')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {paginatedNews.map((news) => (
                                                    <tr key={news.id} className={`md:hover:bg-slate-50/50 transition-colors group ${news.is_reported ? 'bg-rose-50/20' : ''} ${news.is_hidden ? 'opacity-40 grayscale-[0.5]' : ''}`}>
                                                        <td className="px-8 py-6">
                                                            <div className="flex items-center gap-4 max-w-[280px]">
                                                <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 shrink-0 border border-white shadow-sm ring-1 ring-slate-100 flex items-center justify-center relative">
                                                    {news.image ? (
                                                        <img src={news.image} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <ImageIcon size={20} className="text-slate-300" />
                                                    )}
                                                </div>
                                                                <div className="min-w-0">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <span className="text-xs font-black text-slate-900 truncate block uppercase tracking-tight leading-tight">{news.title}</span>
                                                                        {news.is_hidden && (
                                                                            <span className="shrink-0 px-1.5 py-0.5 bg-slate-900 text-white text-[8px] font-black rounded uppercase tracking-widest">{at(lang, 'hidden')}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-6">
                                                            <span className="text-[10px] font-black px-2.5 py-1 bg-slate-100 text-slate-500 rounded-lg uppercase tracking-widest">{news.type}</span>
                                                        </td>
                                                        <td className="px-6 py-6 font-bold text-slate-400 text-[11px] tabular-nums tracking-tighter">
                                                            {news.publishedAt ? new Date(news.publishedAt).toLocaleDateString() : '-'}
                                                        </td>
                                                        <td className="px-8 py-6 text-right">
                                                            <div className="flex items-center justify-end gap-2">
                                                                <button 
                                                                    onClick={() => handleToggleNewsVisibility(news.id, !!news.is_hidden)} 
                                                                    className={`p-2.5 rounded-xl transition-all shadow-sm active:scale-90 border ${news.is_hidden ? 'bg-rose-50 border-rose-100 text-rose-500 md:hover:bg-rose-100' : 'bg-white border-slate-200 text-slate-400 md:hover:text-slate-900 md:hover:border-slate-900'}`}
                                                                    title={news.is_hidden ? at(lang, 'unhide') : at(lang, 'hide')}
                                                                >
                                                                    {news.is_hidden ? <EyeOff size={16} strokeWidth={2.5}/> : <Eye size={16} strokeWidth={2.5}/>}
                                                                </button>
                                                                <button 
                                                                    onClick={() => triggerPreviewModal(true, news)} 
                                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-indigo-600 md:hover:border-indigo-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                                    title={at(lang, 'preview')}
                                                                >
                                                                    <FileText size={16}/>
                                                                </button>
                                                                <button 
                                                                    onClick={() => triggerEditModal(true, news)} 
                                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-amber-600 md:hover:border-amber-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                                     title={at(lang, 'edit')}
                                                                >
                                                                    <Edit2 size={16}/>
                                                                </button>
                                                                <button onClick={() => handleViewNewsReports(news)} className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-indigo-600 md:hover:border-indigo-600 rounded-xl shadow-sm transition-all active:scale-90"><ExternalLink size={16}/></button>
                                                                <button onClick={() => openConfirm('DELETE_NEWS', news)} className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-xl shadow-sm transition-all active:scale-90"><Trash2 size={16}/></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    <div className="p-8 border-t border-slate-100 flex items-center justify-between bg-slate-50/20">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Page {contentCurrentPage} of {totalContentPages || 1}</p>
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={() => setContentCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={contentCurrentPage === 1}
                                                className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                                            >
                                                <ChevronLeft size={18} />
                                            </button>
                                            <button 
                                                onClick={() => setContentCurrentPage(p => Math.min(totalContentPages, p + 1))}
                                                disabled={totalContentPages === 0 || contentCurrentPage === totalContentPages}
                                                className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                                            >
                                                <ChevronRight size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'notices' && <NoticesTab />}

                        {activeTab === 'highlights' && <HighlightsTab />}

                        {activeTab === 'shorts' && <ShortsTab />}

                        {activeTab === 'gameStore' && <GameStoreTab />}

                        {activeTab === 'footyMaster' && <FootyMasterTab />}

                        {activeTab === 'toto' && <TotoTab />}

                        {activeTab === 'inquiries' && <InquiriesTab />}

                        {activeTab === 'reports' && <ReportsTab />}

                        {activeTab === 'settings' && <SettingsTab />}
                    </div>
                </main>
            </div>

            {/* --- Modals & Overlays --- */}

            <AdminActionModal 
                isOpen={actionModal.isOpen}
                isAnimating={isActionAnimating}
                isPending={isActionPending}
                type={actionModal.type}
                title={actionModal.title}
                description={actionModal.description}
                confirmLabel={actionModal.confirmLabel}
                isDanger={actionModal.isDanger}
                awardAmount={awardAmount}
                setAwardAmount={setAwardAmount}
                awardReason={awardReason}
                setAwardReason={setAwardReason}
                onClose={() => triggerActionModal(false)}
                onConfirm={handleActionConfirm}
                lang={lang}
            />

            <AdminUserAuditModal 
                isOpen={isAuditModalOpen}
                isAnimating={isAuditAnimating}
                isLoading={isDetailLoading}
                selectedUser={selectedUser}
                auditTab={auditTab}
                setAuditTab={setAuditTab}
                auditStats={auditStats}
                auditDateFilter={auditDateFilter}
                setAuditDateFilter={setAuditDateFilter}
                auditPageSize={auditPageSize}
                setAuditPageSize={setAuditPageSize}
                auditPage={auditPage}
                setAuditPage={setAuditPage}
                totalAuditPages={totalAuditPages}
                paginatedAuditItems={paginatedAuditItems}
                activeAuditList={activeAuditList}
                onClose={() => triggerAuditModal(false)}
                lang={lang}
            />

            <AdminNewsModals 
                isPreviewOpen={isPreviewOpen}
                isPreviewAnimating={isPreviewAnimating}
                isEditOpen={isEditOpen}
                isEditAnimating={isEditAnimating}
                isActionPending={isActionPending}
                selectedNews={selectedNews}
                editFormData={editFormData}
                setEditFormData={setEditFormData}
                onClosePreview={() => triggerPreviewModal(false)}
                onCloseEdit={() => triggerEditModal(false)}
                onUpdateNews={handleUpdateNews}
                lang={lang}
            />

            <AdminNewsAuditModal 
                isOpen={isNewsAuditOpen}
                isAnimating={isNewsAuditAnimating}
                isLoading={isReportsLoading}
                selectedNews={selectedNews}
                newsReports={newsReports}
                onClose={() => triggerNewsAuditModal(false)}
                onDeleteNews={(news) => openConfirm('DELETE_NEWS', news)}
                lang={lang}
            />

            <AnalysisModal 
                isOpen={isAnalysisModalOpen}
                onClose={() => setIsAnalysisModalOpen(false)}
                editData={selectedNews}
                onSuccess={handleRefresh}
            />

            {/* Alerts Side Drawer */}
            <div className={`fixed inset-0 z-[400] transition-opacity duration-500 ${isAlertsOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleCloseAlerts}></div>
                <div className={`absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-500 ease-[0.32,0.72,0,1] ${isAlertsAnimating ? 'translate-x-0' : 'translate-x-full'}`}>
                    <header className="p-8 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center shadow-sm">
                                <Bell size={24} strokeWidth={2.5} />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-none">{at(lang, 'alerts')}</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{at(lang, 'threeDaysAlert')}</p>
                            </div>
                        </div>
                        <button onClick={handleCloseAlerts} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors"><X size={28} strokeWidth={2.5} /></button>
                    </header>

                    <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar bg-slate-50/30">
                        {loadingAlerts ? (
                            <div className="h-full flex flex-col items-center justify-center gap-4">
                                <Loader2 className="animate-spin text-rose-500" size={32} />
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest animate-pulse">{at(lang, 'syncingAlerts')}</p>
                            </div>
                        ) : alerts.length > 0 ? (
                            alerts.map((alert) => (
                                <div 
                                    key={alert.id} 
                                    className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col gap-1 md:hover:shadow-md transition-all group cursor-pointer active:scale-[0.98]"
                                    onClick={() => {
                                        if (alert.type === 'inquiry') {
                                            setActiveTab('inquiries');
                                            handleCloseAlerts();
                                        }
                                    }}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className={`text-[9px] font-black uppercase tracking-widest ${
                                            alert.type === 'report' ? 'text-rose-500' : 
                                            alert.type === 'inquiry' ? 'text-indigo-500' : 
                                            alert.type === 'user' ? 'text-emerald-500' :
                                            'text-blue-500'
                                        }`}>
                                            {alert.type === 'report' ? at(lang, 'securityAlerts') : 
                                             alert.type === 'inquiry' ? at(lang, 'inquiries') : 
                                             alert.type === 'user' && (alert.id.startsWith('np-') ? at(lang, 'intelligence') : at(lang, 'users'))}
                                        </span>
                                        <span className="text-[9px] font-black text-slate-300 tabular-nums uppercase">{calculateTimeAgo(alert.time)}</span>
                                    </div>
                                    <p className="text-xs font-black text-slate-800 uppercase tracking-tight truncate md:group-hover:text-rose-600 transition-colors">{alert.title}</p>
                                    <p className="text-[10px] font-bold text-slate-400 line-clamp-1">
                                        {alert.type === 'inquiry' ? at(lang, alert.data?.status || 'pending') : (alert.data?.reason || alert.data?.email || '-')}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center opacity-30 gap-4">
                                <Bell size={48} className="text-slate-200" />
                                <p className="text-sm font-black uppercase tracking-widest">{at(lang, 'noAlerts')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {actionModal.isOpen && (
                <div className={`fixed inset-0 z-[450] flex items-center justify-center p-6 transition-opacity duration-500 ${isActionAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => triggerActionModal(false)}></div>
                    <div className={`relative bg-white w-full max-w-sm rounded-[32px] shadow-2xl p-8 transition-transform duration-500 ${isActionAnimating ? 'scale-100' : 'scale-90'}`}>
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${actionModal.isDanger ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-500'}`}>
                            {actionModal.isDanger ? <ShieldAlert size={28} /> : actionModal.type === 'AWARD_POINTS' ? <Coins size={28} className="text-amber-500" /> : <CheckCircle2 size={28} />}
                        </div>
                        <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">{actionModal.title}</h3>
                        <p className="text-sm text-slate-500 font-medium leading-relaxed mb-8">{actionModal.description}</p>
                        
                        {actionModal.type === 'AWARD_POINTS' && (
                            <div className="space-y-4 mb-8 text-left">
                                <div>
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1.5 block">{at(lang, 'awardAmount')}</label>
                                    <input 
                                        type="number" 
                                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 font-bold text-slate-900 focus:bg-white transition-all outline-none"
                                        value={awardAmount}
                                        onChange={e => setAwardAmount(Number(e.target.value))}
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1.5 block">{at(lang, 'awardReason')}</label>
                                    <input 
                                        type="text" 
                                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 font-bold text-slate-900 focus:bg-white transition-all outline-none"
                                        value={awardReason}
                                        onChange={e => setAwardReason(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button onClick={() => triggerActionModal(false)} className="flex-1 py-3 text-xs font-black text-slate-400 uppercase tracking-widest">{at(lang, 'cancel')}</button>
                            <button 
                                onClick={handleActionConfirm} 
                                disabled={isActionPending}
                                className={`flex-[2] py-3 rounded-xl text-xs font-black uppercase tracking-widest text-white shadow-lg active:scale-95 transition-all ${actionModal.isDanger ? 'bg-rose-500 shadow-rose-100 md:hover:bg-rose-600' : 'bg-slate-900 shadow-slate-100 md:hover:bg-black'}`}
                            >
                                {isActionPending ? <Loader2 className="animate-spin mx-auto" size={18} /> : actionModal.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isAuditModalOpen && selectedUser && (
                <div className={`fixed inset-0 z-[400] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isAuditAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => triggerAuditModal(false)}></div>
                    <div className={`relative bg-white w-full max-w-4xl rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden h-[90vh] md:h-[85vh] transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAuditAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-100 shadow-sm shrink-0">
                                    <img src={selectedUser.avatar || `https://ui-avatars.com/api/?name=${selectedUser.name}`} className="w-full h-full object-cover" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-none mb-1 truncate">{selectedUser.name}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{at(lang, 'personnelActivityLogs')}</p>
                                </div>
                            </div>
                            <button onClick={() => triggerAuditModal(false)} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors shrink-0"><X size={24} /></button>
                        </header>
                        
                        <div className="bg-slate-50/50 border-b border-slate-100 px-8 py-4 flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm w-full md:w-auto">
                                {[
                                    { id: 'news', label: at(lang, 'news'), count: auditStats.news, icon: Newspaper },
                                    { id: 'news_comments', label: at(lang, 'newsTalk'), count: auditStats.news_comments, icon: MessageSquare },
                                    { id: 'match_comments', label: at(lang, 'matchTalk'), count: auditStats.match_comments, icon: Trophy }
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => { setAuditTab(tab.id as UserAuditTab); setAuditPage(1); }}
                                        className={`flex-1 md:flex-initial flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                            auditTab === tab.id 
                                            ? 'bg-slate-900 text-white shadow-md' 
                                            : 'text-slate-400 md:hover:text-slate-600'
                                        }`}
                                    >
                                        <tab.icon size={14} />
                                        <span className="hidden sm:inline">{tab.label}</span>
                                        <span className={`px-1.5 py-0.5 rounded-md text-[8px] font-black ${auditTab === tab.id ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-500'}`}>{tab.count}</span>
                                    </button>
                                ))}
                            </div>

                            <div className="flex items-center gap-4 w-full md:w-auto">
                                <div className="relative flex-1 md:w-48 group">
                                    <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-rose-500" size={14} />
                                    <input 
                                        type="date" 
                                        className="w-full bg-white border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 text-[10px] font-black uppercase text-slate-700 outline-none shadow-sm cursor-pointer focus:border-rose-500 transition-colors"
                                        value={auditDateFilter}
                                        onChange={e => { setAuditDateFilter(e.target.value); setAuditPage(1); }}
                                    />
                                    {auditDateFilter && (
                                        <button onClick={() => setAuditDateFilter('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-300 md:hover:text-rose-500"><X size={12}/></button>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <select 
                                        value={auditPageSize}
                                        onChange={(e) => { setAuditPageSize(Number(e.target.value)); setAuditPage(1); }}
                                        className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-[10px] font-black uppercase tracking-widest outline-none shadow-sm cursor-pointer"
                                    >
                                        <option value={10}>10 Items</option>
                                        <option value={30}>30 Items</option>
                                        <option value={50}>50 Items</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                            {isDetailLoading ? (
                                <div className="h-full flex flex-col items-center justify-center gap-4">
                                    <Loader2 className="animate-spin text-rose-500" size={32} />
                                    <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest animate-pulse">{at(lang, 'syncingLogs')}</span>
                                </div>
                            ) : paginatedAuditItems.length > 0 ? (
                                <div className="space-y-4 animate-in fade-in duration-500">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 px-2">{at(lang, 'allActivities')} : {at(lang, 'foundCount').replace('{{count}}', activeAuditList.length.toString())}</p>
                                    {paginatedAuditItems.map((item, i) => (
                                        <div key={i} className="p-5 bg-white border border-slate-100 rounded-3xl flex items-start gap-4 md:hover:shadow-md md:hover:border-slate-200 transition-all group">
                                            <div className="p-2.5 bg-slate-50 rounded-2xl text-slate-300 md:group-hover:text-rose-500 md:group-hover:bg-rose-50 transition-colors shrink-0">
                                                {auditTab === 'news' ? <Newspaper size={18}/> : auditTab === 'news_comments' ? <MessageSquare size={18}/> : <Trophy size={18}/>}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-bold text-slate-700 leading-relaxed mb-2 uppercase tracking-tight">
                                                    {auditTab === 'news' ? item.title : item.content}
                                                </p>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest tabular-nums flex items-center gap-1.5">
                                                        <Clock size={10} className="text-rose-400" />
                                                        {new Date(item.created_at || item.published_at).toLocaleString()}
                                                    </span>
                                                    {auditTab === 'news' && item.type && (
                                                        <span className="text-[8px] font-black px-2 py-0.5 bg-slate-100 text-slate-400 rounded-md uppercase tracking-tighter">{item.type}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => {
                                                    const path = auditTab === 'news' || auditTab === 'news_comments' ? `/feed/item/${item.id || item.news_id}` : `/game/${item.post_id}`;
                                                    window.open(`${window.location.origin}${path}`, '_blank');
                                                }}
                                                className="p-2.5 text-slate-200 md:hover:text-indigo-500 md:hover:bg-indigo-50 rounded-xl transition-all shrink-0"
                                            >
                                                <ExternalLink size={18} strokeWidth={2.5} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-center opacity-30 gap-4">
                                    <div className="p-6 bg-slate-50 rounded-full">
                                        <Info size={48} />
                                    </div>
                                    <p className="text-sm font-black uppercase tracking-widest">{at(lang, 'noActivityLogs')}</p>
                                </div>
                            )}
                        </div>

                        <footer className="p-6 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
                            <div className="flex items-center gap-4">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{at(lang, 'page')} {auditPage} {at(lang, 'of')} {totalAuditPages || 1}</span>
                                <div className="flex items-center gap-2">
                                    <button 
                                        disabled={auditPage === 1}
                                        onClick={() => setAuditPage(p => p - 1)}
                                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-20 transition-all active:scale-90 shadow-sm"
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <button 
                                        disabled={auditPage === totalAuditPages || totalAuditPages === 0}
                                        onClick={() => setAuditPage(p => p + 1)}
                                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-20 transition-all active:scale-90 shadow-sm"
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </footer>
                    </div>
                </div>
            )}

            {isPreviewOpen && selectedNews && (
                <div className={`fixed inset-0 z-[400] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isPreviewAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => triggerPreviewModal(false)}></div>
                    <div className={`relative bg-white w-full max-w-lg rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden h-[80vh] md:h-auto md:max-h-[85vh] transition-transform duration-700 ease-[0.32,0.72,0,1] ${isPreviewAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-6 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{at(lang, 'preview')}</h3>
                            <button onClick={() => triggerPreviewModal(false)} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors shrink-0"><X size={24} /></button>
                        </header>
                        <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50">
                            <div className="max-w-md mx-auto py-8 px-4 space-y-12">
                                <section>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center mb-4">{at(lang, 'appearanceInLiveFeed')}</p>
                                    <div className="bg-white shadow-xl rounded-3xl overflow-hidden border border-slate-100">
                                        <FeedNewsCard 
                                            item={selectedNews} 
                                            index={0} 
                                            lang={lang} 
                                            onClick={() => {}}
                                        />
                                    </div>
                                </section>

                                <section className="bg-white shadow-xl rounded-[40px] overflow-hidden border border-slate-100 flex flex-col">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center py-6 border-b border-slate-50 bg-slate-50/50">{at(lang, 'detailPagePreview')}</p>
                                    <div className="p-8 space-y-8">
                                        <div className="space-y-4">
                                            <h1 className="text-2xl font-black text-slate-900 leading-tight">{selectedNews.title}</h1>
                                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                <span>{selectedNews.source}</span>
                                                <span>·</span>
                                                <span>{selectedNews.time}</span>
                                            </div>
                                        </div>

                                        {selectedNews.image && (
                                            <div className="rounded-[32px] overflow-hidden aspect-[16/9] bg-slate-100 border border-slate-100">
                                                <img src={selectedNews.image} className="w-full h-full object-cover" />
                                            </div>
                                        )}

                                        <div className="bg-rose-50/50 p-6 rounded-3xl border-l-4 border-rose-500 space-y-2">
                                            <p className="text-[9px] font-black text-rose-500 uppercase tracking-widest">Key Briefing</p>
                                            <p className="text-sm font-bold text-slate-700 leading-relaxed">{selectedNews.summary}</p>
                                        </div>

                                        <div className="prose prose-slate max-w-none">
                                            <p className="text-slate-600 text-base leading-relaxed whitespace-pre-wrap font-medium">
                                                {selectedNews.content}
                                            </p>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isEditOpen && selectedNews && (
                <div className={`fixed inset-0 z-[400] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isEditAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => triggerEditModal(false)}></div>
                    <div className={`relative bg-white w-full max-w-2xl rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden h-[90vh] md:h-[85vh] transition-transform duration-700 ease-[0.32,0.72,0,1] ${isEditAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center">
                                    <Edit2 size={24} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">{at(lang, 'editIntelligence')}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">ID: {selectedNews.id}</p>
                                </div>
                            </div>
                            <button onClick={() => triggerEditModal(false)} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors shrink-0"><X size={24} /></button>
                        </header>
                        
                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar space-y-8">
                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">{at(lang, 'titleKo')}</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10"
                                    value={editFormData.title_ko}
                                    onChange={e => setEditFormData({...editFormData, title_ko: e.target.value})}
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">{at(lang, 'summaryKo')}</label>
                                <textarea 
                                    rows={4}
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10 resize-none"
                                    value={editFormData.summary_ko}
                                    onChange={e => setEditFormData({...editFormData, summary_ko: e.target.value})}
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">{at(lang, 'contentKo')}</label>
                                <textarea 
                                    rows={10}
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10 resize-none"
                                    value={editFormData.content_ko}
                                    onChange={e => setEditFormData({...editFormData, content_ko: e.target.value})}
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">{at(lang, 'sector')}</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10"
                                    value={editFormData.type}
                                    onChange={e => setEditFormData({...editFormData, type: e.target.value})}
                                />
                            </div>
                        </div>

                        <footer className="p-8 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                            <button onClick={() => triggerEditModal(false)} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest md:hover:text-slate-900 transition-colors">{at(lang, 'cancel')}</button>
                            <button 
                                onClick={handleUpdateNews} 
                                disabled={isActionPending}
                                className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-slate-100 md:hover:bg-black transition-all active:scale-95 disabled:opacity-50"
                            >
                                {isActionPending ? <Loader2 className="animate-spin mx-auto" size={18} /> : (at(lang, 'saveChanges'))}
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {isNewsAuditOpen && selectedNews && (
                <div className={`fixed inset-0 z-[400] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isNewsAuditAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => triggerNewsAuditModal(false)}></div>
                    <div className={`relative bg-white w-full max-w-2xl rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden h-[85vh] md:h-[80vh] transition-transform duration-700 ease-[0.32,0.72,0,1] ${isNewsAuditAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl overflow-hidden shadow-sm flex items-center justify-center bg-slate-100">
                                    {selectedNews.image ? (
                                        <img src={selectedNews.image} className="w-full h-full object-cover" />
                                    ) : (
                                        <ImageIcon size={20} className="text-slate-300" />
                                    )}
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight leading-none mb-1">{at(lang, 'intelligenceBreachAudit')}</h3>
                                    <p className="text-[10px] font-bold text-rose-500 uppercase tracking-widest truncate max-w-[200px]">{selectedNews.title}</p>
                                </div>
                            </div>
                            <button onClick={() => triggerNewsAuditModal(false)} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors shrink-0"><X size={24} /></button>
                        </header>
                        
                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                            {isReportsLoading ? (
                                <div className="h-full flex flex-col items-center justify-center gap-4">
                                    <Loader2 className="animate-spin text-rose-500" size={32} />
                                    <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Retrieving Breach Data...</span>
                                </div>
                            ) : (
                                <div className="space-y-10 animate-in fade-in duration-500">
                                    <section>
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">{at(lang, 'breachOrigin')}</h4>
                                        <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <UserPlus size={18} className="text-slate-400" />
                                                <span className="text-sm font-black text-slate-700">{at(lang, 'source')}: {selectedNews.source}</span>
                                            </div>
                                            <span className="text-[10px] font-black text-rose-500 uppercase px-3 py-1 bg-rose-50 rounded-lg">{at(lang, 'reported')}</span>
                                        </div>
                                    </section>

                                    <section>
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">{at(lang, 'breachTestimony')} ({newsReports.length})</h4>
                                <div className="space-y-3">
                                    {newsReports.map((r, i) => (
                                        <div key={i} className="p-6 bg-white border border-slate-100 rounded-[28px] shadow-sm">
                                            <div className="flex items-center gap-3 mb-3">
                                                <Shield size={14} className="text-rose-500" />
                                                <span className="text-[10px] font-black text-slate-900 uppercase tracking-widest">{at(lang, 'breachReport')} #{i+1}</span>
                                            </div>
                                            <p className="text-sm font-bold text-slate-600 leading-relaxed italic">"{r.reason || at(lang, 'noSpecificReason')}"</p>
                                            <div className="mt-4 pt-4 border-t border-slate-50 flex justify-between items-center text-[9px] font-black text-slate-300 uppercase tracking-widest">
                                                <span>{at(lang, 'scoutId')}: {r.reporter_id.substring(0, 8)}...</span>
                                                <span>{new Date(r.created_at).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    ))}
                                    {newsReports.length === 0 && (
                                        <div className="py-20 text-center opacity-30">
                                            <Shield size={48} className="mx-auto mb-4" />
                                            <p className="text-xs font-black uppercase tracking-widest">{at(lang, 'noTestimonyRecorded')}</p>
                                        </div>
                                    )}
                                </div>
                                    </section>
                                </div>
                            )}
                        </div>
                        
                        <footer className="p-8 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                            <button onClick={() => triggerNewsAuditModal(false)} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest md:hover:text-slate-900 transition-colors">{at(lang, 'closeLog')}</button>
                            <button onClick={() => openConfirm('DELETE_NEWS', selectedNews)} className="flex-[2] py-4 bg-rose-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-rose-100 md:hover:bg-rose-600 transition-all active:scale-95">{at(lang, 'terminateIntelligence')}</button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
};
